import * as XLSX from 'xlsx';
import { splitFinancialData } from './financialSplitter';

/**
 * Professionel Excel-eksport til Fjernvarme Skælskør
 *
 * Features:
 * - Multiple sheets: Master (med formler), Rådata, Varme, El
 * - Dansk formatering med æøå support
 * - Formler der linker mellem sheets
 * - Struktureret layout med headers og kategorier
 */

/**
 * Formaterer tal til Excel med 1 decimal
 */
const formatNumber = (value, decimals = 1) => {
  if (value === null || value === undefined || isNaN(value)) return 0;
  return Number(value.toFixed(decimals));
};

/**
 * Generer Master tab med direkte værdier fra financialData
 */
const generateMasterSheet = (financialData, stats) => {
  const data = [];
  const heat = financialData.heat;
  const elec = financialData.electricity;

  // Header
  data.push(['FJERNVARME SKÆLSKØR - FINANSIEL MASTER OVERSIGT']);
  data.push(['Genereret:', new Date().toLocaleDateString('da-DK')]);
  data.push([]);

  // Sektion: Samlet Resultatopgørelse
  data.push(['SAMLET RESULTATOPGØRELSE (DKK)']);
  data.push(['Beskrivelse', 'Varmeselskab', 'Elselskab', 'Total']);
  data.push([
    'Indtægter',
    formatNumber(heat.totalRevenue, 0),
    formatNumber(elec.totalRevenue, 0),
    formatNumber(heat.totalRevenue + elec.totalRevenue, 0)
  ]);
  data.push([
    'OPEX (Driftsomkostninger)',
    formatNumber(heat.totalOpex, 0),
    formatNumber(elec.totalOpex, 0),
    formatNumber(heat.totalOpex + elec.totalOpex, 0)
  ]);
  data.push([
    'EBITDA',
    formatNumber(heat.ebitda, 0),
    formatNumber(elec.ebitda, 0),
    formatNumber(heat.ebitda + elec.ebitda, 0)
  ]);
  data.push([
    'Afskrivninger',
    formatNumber(heat.totalDepreciation * 1000000, 0),
    formatNumber(elec.totalDepreciation * 1000000, 0),
    formatNumber((heat.totalDepreciation + elec.totalDepreciation) * 1000000, 0)
  ]);
  data.push([
    'Nettoresultat',
    formatNumber(heat.netProfit, 0),
    formatNumber(elec.netProfit, 0),
    formatNumber(heat.netProfit + elec.netProfit, 0)
  ]);
  data.push([]);

  // Sektion: CAPEX Investeringer
  data.push(['CAPEX INVESTERINGER (Millioner DKK)']);
  data.push(['Beskrivelse', 'Varmeselskab', 'Elselskab', 'Total']);
  data.push([
    'HTES (Hot Thermal Energy Storage)',
    formatNumber(heat.capex.htes, 1),
    0,
    formatNumber(heat.capex.htes, 1)
  ]);
  data.push([
    'LTES (Low Temperature Energy Storage)',
    formatNumber(heat.capex.ltes, 1),
    0,
    formatNumber(heat.capex.ltes, 1)
  ]);
  data.push([
    'Elkedel (Electric Boiler)',
    0,
    formatNumber(elec.capex.elkedel, 1),
    formatNumber(elec.capex.elkedel, 1)
  ]);
  data.push([
    'Batterier (Battery Storage)',
    0,
    formatNumber(elec.capex.batterier, 1),
    formatNumber(elec.capex.batterier, 1)
  ]);
  data.push([
    'Total CAPEX',
    formatNumber(heat.totalCapex, 1),
    formatNumber(elec.totalCapex, 1),
    formatNumber(heat.totalCapex + elec.totalCapex, 1)
  ]);
  data.push([]);

  // Sektion: Nøgletal
  data.push(['NØGLETAL']);
  data.push(['Beskrivelse', 'Værdi', 'Enhed']);
  data.push(['NPV (Nutidsværdi)', formatNumber((stats.npv || 0) / 1000000, 1), 'Millioner DKK']);
  data.push(['IRR (Intern Rente)', formatNumber(stats.irr || 0, 1), '%']);
  data.push(['LCOE (Levelized Cost of Energy)', formatNumber(stats.lcoe || 0, 0), 'DKK/MWh']);
  data.push(['Payback Period', formatNumber(stats.paybackPeriod || 0, 1), 'År']);
  const nettomargin = (heat.totalRevenue + elec.totalRevenue) > 0 ?
    ((heat.netProfit + elec.netProfit) / (heat.totalRevenue + elec.totalRevenue) * 100) : 0;
  data.push(['Nettomargin', formatNumber(nettomargin, 1), '%']);
  data.push([]);

  // Sektion: Varmepriser
  data.push(['VARMEPRISER (DKK/MWh)']);
  data.push(['Niveau', 'Værdi']);
  data.push(['Anlægspris (Plant Price)', formatNumber(stats.plantPricePerMWh || 0, 0)]);
  data.push(['Total Råpris (Total Raw Price)', formatNumber(stats.totalRawCostPerMWh || 0, 0)]);
  data.push(['OPEX Pris (med driftsomkostninger)', formatNumber(stats.opexPricePerMWh || 0, 0)]);
  data.push(['Kundepris (Customer Price)', formatNumber(stats.customerPricePerMWh || 0, 0)]);
  data.push([]);

  // Instruktioner
  data.push(['INSTRUKTIONER:']);
  data.push(['- Denne Master tab viser samlet overblik over hele projektet']);
  data.push(['- Se "Varme" tab for detaljeret varmeselskab finansiering']);
  data.push(['- Se "El" tab for detaljeret elselskab finansiering']);
  data.push(['- Se "Rådata" tab for time-for-time simuleringsdata (8783 timer)']);
  data.push(['- Alle værdier kan redigeres i kildetabs hvis nødvendigt']);

  return data;
};

/**
 * Aggreger actions fra hourly data til strukturerede kolonner
 */
const aggregateHourlyActions = (hour) => {
  const result = {
    heaterOutput: 0,
    heaterToHTES: 0,
    heaterToLTES: 0,
    htesCharge: 0,
    htesDischarge: 0,
    htesToLTES: 0,
    ltesDischargeToDH: 0,
    ltesChargeGrid: 0,
    ltesChargeSolar: 0,
    ltesChargeWaste: 0,
    turbineInput: 0,
    turbineHeat: 0,
    turbineElectricity: 0,
    batteryCharge: 0,
    batteryDischarge: 0,
    gridPurchaseOp: 0,
    gridPurchaseArb: 0,
    gridSale: 0,
    solarPurchaseOp: 0,
    solarPurchaseArb: 0,
    wasteHeatAccepted: 0,
    heaterServiceRevenue: 0,
    batteryServiceRevenue: 0,
    turbineServiceRevenue: 0,
  };

  if (!hour.actions || !Array.isArray(hour.actions)) {
    return result;
  }

  // Loop gennem alle actions og aggreger
  hour.actions.forEach(action => {
    switch (action.type) {
      case 'HEATER_DIRECT_TO_DH':
        result.heaterOutput += action.amount || 0;
        break;
      case 'HEATER_TO_HTES':
        result.heaterToHTES += action.amount || 0;
        result.htesCharge += action.amount || 0;
        break;
      case 'HEATER_TO_LTES':
        result.heaterToLTES += action.amount || 0;
        break;
      case 'DISCHARGE_HTES_TO_DH':
        result.htesDischarge += action.amount || 0;
        break;
      case 'DISCHARGE_HTES_TURBINE':
        result.htesDischarge += action.input || 0;
        result.turbineInput += action.input || 0;
        result.turbineHeat += action.heat || 0;
        result.turbineElectricity += action.electricity || 0;
        result.gridSale += action.electricity || 0;
        if (action.revenue) result.turbineServiceRevenue += action.revenue;
        break;
      case 'DISCHARGE_HTES_TO_LTES':
        result.htesDischarge += action.amount || 0;
        result.htesToLTES += action.amount || 0;
        break;
      case 'DISCHARGE_LTES_TO_DH':
        result.ltesDischargeToDH += action.amount || 0;
        break;
      case 'OP_CHARGE_LTES_GRID':
        result.ltesChargeGrid += action.amount || 0;
        result.gridPurchaseOp += action.amount || 0;
        break;
      case 'OP_CHARGE_LTES_SOLAR':
        result.ltesChargeSolar += action.amount || 0;
        result.solarPurchaseOp += action.amount || 0;
        break;
      case 'CHARGE_LTES_WASTE_HEAT':
        result.ltesChargeWaste += action.amount || 0;
        result.wasteHeatAccepted += action.amount || 0;
        break;
      case 'ARB_BATTERY_CHARGE':
      case 'ARB_BATTERY_CHARGE_FORCED':
        result.batteryCharge += action.amount || 0;
        result.gridPurchaseArb += action.amount || 0;
        break;
      case 'ARB_BATTERY_DISCHARGE':
        result.batteryDischarge += action.amount || 0;
        result.gridSale += action.output || 0;
        if (action.revenue) result.batteryServiceRevenue += action.revenue;
        break;
      case 'HEATER_SERVICES':
        if (action.revenue) result.heaterServiceRevenue += action.revenue;
        break;
      case 'BATTERY_SERVICES':
        if (action.revenue) result.batteryServiceRevenue += action.revenue;
        break;
      case 'TURBINE_SERVICES':
        if (action.revenue) result.turbineServiceRevenue += action.revenue;
        break;
    }
  });

  return result;
};

/**
 * Generer Rådata sheet med alle hourly actions
 */
const generateRawDataSheet = (hourlyData, stats, config) => {
  const data = [];

  // Metadata først (til formler i Master)
  data.push(['NPV', stats.npv || 0]);
  data.push(['IRR', stats.irr || 0]);
  data.push(['LCOE', stats.lcoe || 0]);
  data.push(['Payback', stats.paybackPeriod || 0]);
  data.push([]);

  // Header for time-data med danske navne
  data.push([
    'Time',
    'Måned',
    'Dag',
    'Time på døgn',
    'Spotpris (DKK/MWh)',
    'Sol tilgængelig (MWh)',
    'Varmebehov (MW)',
    'Overskudsvarme tilgængelig (MW)',
    'Elkedel til DH (MW)',
    'Elkedel til HTES (MW)',
    'Elkedel til LTES (MW)',
    'HTES Opladning (MW)',
    'HTES Afladning (MW)',
    'HTES til LTES (MW)',
    'HTES SOC (%)',
    'LTES Afladning til DH (MW)',
    'LTES Opladning Grid (MW)',
    'LTES Opladning Sol (MW)',
    'LTES Opladning Overskudsvarme (MW)',
    'LTES SOC (%)',
    'Turbine Input (MW)',
    'Turbine Varme (MW)',
    'Turbine El (MW)',
    'Batteri Opladning (MW)',
    'Batteri Afladning (MW)',
    'Batteri SOC (%)',
    'Grid Køb Operational (MW)',
    'Grid Køb Arbitrage (MW)',
    'Grid Salg (MW)',
    'Sol Køb Operational (MWh)',
    'Sol Køb Arbitrage (MWh)',
    'Overskudsvarme Accepteret (MW)',
    'Elkedel Systemydelser (DKK)',
    'Batteri Systemydelser (DKK)',
    'Turbine Systemydelser (DKK)',
    'Råomkostning (DKK)',
    'Indtægt (DKK)',
    'Nettoomkostning (DKK)',
  ]);

  // Time-for-time data
  if (Array.isArray(hourlyData) && hourlyData.length > 0) {
    hourlyData.forEach((hour) => {
      // Aggreger alle actions
      const agg = aggregateHourlyActions(hour);

      // Storage levels fra hour.storage objekt
      const htesSOC = hour.storage?.htes_level || 0;
      const ltesSOC = hour.storage?.ltes_level || 0;
      const batterySOC = hour.storage?.battery_level || 0;

      // Costs fra hour.costs objekt
      const rawCost = hour.costs?.raw || 0;
      const revenue = hour.costs?.revenue || 0;
      const netCost = hour.costs?.net || 0;

      data.push([
        hour.hour + 1,
        hour.month || 0,
        hour.day || 0,
        hour.hourOfDay || 0,
        formatNumber(hour.spotPrice || 0, 2),
        formatNumber(hour.solarMWh || 0, 2),
        formatNumber(hour.demandMW || 0, 2),
        formatNumber(hour.wasteHeatMW || 0, 2),
        formatNumber(agg.heaterOutput, 2),
        formatNumber(agg.heaterToHTES, 2),
        formatNumber(agg.heaterToLTES, 2),
        formatNumber(agg.htesCharge, 2),
        formatNumber(agg.htesDischarge, 2),
        formatNumber(agg.htesToLTES, 2),
        formatNumber(htesSOC, 1),
        formatNumber(agg.ltesDischargeToDH, 2),
        formatNumber(agg.ltesChargeGrid, 2),
        formatNumber(agg.ltesChargeSolar, 2),
        formatNumber(agg.ltesChargeWaste, 2),
        formatNumber(ltesSOC, 1),
        formatNumber(agg.turbineInput, 2),
        formatNumber(agg.turbineHeat, 2),
        formatNumber(agg.turbineElectricity, 2),
        formatNumber(agg.batteryCharge, 2),
        formatNumber(agg.batteryDischarge, 2),
        formatNumber(batterySOC, 1),
        formatNumber(agg.gridPurchaseOp, 2),
        formatNumber(agg.gridPurchaseArb, 2),
        formatNumber(agg.gridSale, 2),
        formatNumber(agg.solarPurchaseOp, 2),
        formatNumber(agg.solarPurchaseArb, 2),
        formatNumber(agg.wasteHeatAccepted, 2),
        formatNumber(agg.heaterServiceRevenue, 0),
        formatNumber(agg.batteryServiceRevenue, 0),
        formatNumber(agg.turbineServiceRevenue, 0),
        formatNumber(rawCost, 0),
        formatNumber(revenue, 0),
        formatNumber(netCost, 0),
      ]);
    });
  } else {
    // Hvis ingen data, tilføj placeholder
    data.push(['Ingen simuleringsdata tilgængelig - Kør simulering først']);
  }

  return data;
};

/**
 * Generer Varme sheet med detaljeret finansiel opdeling
 */
const generateHeatSheet = (financialData, stats) => {
  const data = [];
  const heat = financialData.heat;

  // Header
  data.push(['VARMESELSKAB - SKÆLSKØR FJERNVARME']);
  data.push([]);

  // Indtægter
  data.push(['INDTÆGTER (DKK)']);
  data.push(['Post', 'Beløb']);
  data.push(['Varmesalg (Kunde niveau)', formatNumber(heat.revenue.varmesalg, 0)]);
  data.push(['Andre indtægter', formatNumber(heat.revenue.andre || 0, 0)]);
  data.push(['Total Indtægt', formatNumber(heat.totalRevenue, 0)]);
  data.push([]);

  // OPEX
  data.push(['OPEX - DRIFTSOMKOSTNINGER (DKK)']);
  data.push(['Post', 'Beløb']);
  data.push(['Elkøb (Operational - til varmeproduktion)', formatNumber(heat.opex.elkøbOperational, 0)]);
  data.push(['Solkøb (Operational)', formatNumber(heat.opex.solkøbOperational, 0)]);
  data.push(['Overskudsvarme (Fabrik)', formatNumber(heat.opex.overskudsvarme, 0)]);
  data.push(['Vedligeholdelse og drift', formatNumber(heat.opex.vedligeholdelse || 0, 0)]);
  data.push(['Total OPEX', formatNumber(heat.totalOpex, 0)]);
  data.push([]);

  // Afskrivninger
  data.push(['AFSKRIVNINGER (Millioner DKK/år)']);
  data.push(['Post', 'Beløb / år']);
  Object.entries(heat.depreciation).forEach(([key, value]) => {
    if (value > 0) {
      const labels = {
        htes: 'HTES / 25 år',
        ltes: 'LTES / 25 år',
        turbine: 'Turbine / 15 år',
        ledningsnet: 'Ledningsnet / 50 år',
        bygninger: 'Bygninger / 50 år',
        orc: 'ORC / 15 år',
        tilkoblinger: 'Tilkoblinger / 50 år',
        industrigrund: 'Industrigrund / 50 år',
        veksler: 'Veksler / 25 år',
        software: 'Software / 7 år',
      };
      data.push([labels[key] || key, formatNumber(value, 2)]);
    }
  });
  data.push(['Total Afskrivning', formatNumber(heat.totalDepreciation, 2)]);
  data.push([]);

  // CAPEX
  data.push(['CAPEX - INVESTERINGER (Millioner DKK)']);
  data.push(['Post', 'Beløb']);
  Object.entries(heat.capex).forEach(([key, value]) => {
    if (value > 0) {
      const labels = {
        htes: 'HTES (Hot Thermal Energy Storage)',
        ltes: 'LTES (Low Temperature Energy Storage)',
        tilslutning: 'Tilslutning til elnet',
        turbine: 'Turbine 10 MW',
        ledningsnet: 'Ledningsnet infrastruktur',
        bygninger: 'Bygninger',
        orc: 'ORC anlæg',
        projektledelse: 'Projektledelse',
        software: 'Software',
        diverse: 'Diverse',
        tilkoblinger: 'Tilkoblinger (1475 stk)',
        industrigrund: 'Industrigrund',
        veksler: 'Veksler til overskudsvarme',
      };
      data.push([labels[key] || key, formatNumber(value, 1)]);
    }
  });
  data.push(['Total CAPEX', formatNumber(heat.totalCapex, 1)]);
  data.push([]);

  // Varmepriser
  data.push(['VARMEPRISER (DKK/MWh)']);
  data.push(['Niveau', 'Pris']);
  data.push(['Anlægspris (Plant Price)', formatNumber(stats.plantPricePerMWh, 2)]);
  data.push(['Total Råpris (Total Raw Price)', formatNumber(stats.totalRawCostPerMWh, 2)]);
  data.push(['OPEX Pris (med driftsomkostninger)', formatNumber(stats.opexPricePerMWh, 2)]);
  data.push(['Kundepris (Customer Price)', formatNumber(stats.customerPricePerMWh, 2)]);
  data.push([]);

  // P&L Summary
  data.push(['RESULTATOPGØRELSE (P&L)']);
  data.push(['Post', 'Beløb']);
  data.push(['Indtægt', formatNumber(heat.totalRevenue, 0)]);
  data.push(['OPEX', formatNumber(-heat.totalOpex, 0)]);
  data.push(['EBITDA', formatNumber(heat.ebitda, 0)]);
  data.push(['Afskrivninger', formatNumber(-heat.totalDepreciation * 1000000, 0)]);
  data.push(['Nettoresultat', formatNumber(heat.netProfit, 0)]);
  data.push(['Nettomargin (%)', formatNumber(heat.totalRevenue > 0 ? (heat.netProfit / heat.totalRevenue * 100) : 0, 1)]);

  return data;
};

/**
 * Generer El sheet med detaljeret finansiel opdeling
 */
const generateElectricitySheet = (financialData, stats) => {
  const data = [];
  const elec = financialData.electricity;

  // Header
  data.push(['ELSELSKAB - SKÆLSKØR ENERGI']);
  data.push([]);

  // Indtægter
  data.push(['INDTÆGTER (DKK)']);
  data.push(['Post', 'Beløb']);
  data.push(['Elsalg (Turbine)', formatNumber(elec.revenue.elsalgTurbine, 0)]);
  data.push(['Elsalg (Batteri - Arbitrage)', formatNumber(elec.revenue.elsalgBatteri, 0)]);
  data.push(['Systemydelser (Elkedel - mFRR/aFRR)', formatNumber(elec.revenue.systemydelserElkedel, 0)]);
  data.push(['Systemydelser (Batteri)', formatNumber(elec.revenue.systemydelserBatteri, 0)]);
  data.push(['Systemydelser (Turbine)', formatNumber(elec.revenue.systemydelserTurbine, 0)]);
  data.push(['Kapacitetsbetaling', formatNumber(elec.revenue.kapacitetsbetaling, 0)]);
  data.push(['Total Indtægt', formatNumber(elec.totalRevenue, 0)]);
  data.push([]);

  // OPEX
  data.push(['OPEX - DRIFTSOMKOSTNINGER (DKK)']);
  data.push(['Post', 'Beløb']);
  data.push(['Elkøb (Arbitrage - til batteri trading)', formatNumber(elec.opex.elkøbArbitrage, 0)]);
  data.push(['Solkøb (Arbitrage)', formatNumber(elec.opex.solkøbArbitrage || 0, 0)]);
  data.push(['Vedligeholdelse og drift', formatNumber(elec.opex.vedligeholdelse || 0, 0)]);
  data.push(['Total OPEX', formatNumber(elec.totalOpex, 0)]);
  data.push([]);

  // Afskrivninger
  data.push(['AFSKRIVNINGER (Millioner DKK/år)']);
  data.push(['Post', 'Beløb / år']);
  Object.entries(elec.depreciation).forEach(([key, value]) => {
    if (value > 0) {
      const labels = {
        elkedel: 'Elkedel / 25 år',
        tilslutning: 'Tilslutning / 50 år',
        batterier: 'Batterier / 10 år',
      };
      data.push([labels[key] || key, formatNumber(value, 2)]);
    }
  });
  data.push(['Total Afskrivning', formatNumber(elec.totalDepreciation, 2)]);
  data.push([]);

  // CAPEX
  data.push(['CAPEX - INVESTERINGER (Millioner DKK)']);
  data.push(['Post', 'Beløb']);
  Object.entries(elec.capex).forEach(([key, value]) => {
    if (value > 0) {
      const labels = {
        elkedel: 'Elkedel (Electric Boiler)',
        tilslutning: 'Tilslutning til elnet',
        batterier: 'Batterier (Battery Storage)',
      };
      data.push([labels[key] || key, formatNumber(value, 1)]);
    }
  });
  data.push(['Total CAPEX', formatNumber(elec.totalCapex, 1)]);
  data.push([]);

  // P&L Summary
  data.push(['RESULTATOPGØRELSE (P&L)']);
  data.push(['Post', 'Beløb']);
  data.push(['Indtægt', formatNumber(elec.totalRevenue, 0)]);
  data.push(['OPEX', formatNumber(-elec.totalOpex, 0)]);
  data.push(['EBITDA', formatNumber(elec.ebitda, 0)]);
  data.push(['Afskrivninger', formatNumber(-elec.totalDepreciation * 1000000, 0)]);
  data.push(['Nettoresultat', formatNumber(elec.netProfit, 0)]);
  data.push(['Nettomargin (%)', formatNumber(elec.totalRevenue > 0 ? (elec.netProfit / elec.totalRevenue * 100) : 0, 1)]);

  return data;
};

/**
 * Hovedfunktion til at eksportere komplet finansiel rapport som Excel
 */
export const exportCompleteFinancialReport = (stats, config, hourlyData) => {
  try {
    // Split financial data
    const financialData = splitFinancialData(stats, config);

    // Opret workbook
    const wb = XLSX.utils.book_new();

    // Generer alle sheets
    const masterData = generateMasterSheet(financialData, stats);
    const rawData = generateRawDataSheet(hourlyData, stats, config);
    const heatData = generateHeatSheet(financialData, stats);
    const elecData = generateElectricitySheet(financialData, stats);

    // Konverter til worksheets
    const wsMaster = XLSX.utils.aoa_to_sheet(masterData);
    const wsRaw = XLSX.utils.aoa_to_sheet(rawData);
    const wsHeat = XLSX.utils.aoa_to_sheet(heatData);
    const wsElec = XLSX.utils.aoa_to_sheet(elecData);

    // Sæt kolonnebredder for bedre læsbarhed
    wsMaster['!cols'] = [{ wch: 35 }, { wch: 18 }, { wch: 18 }, { wch: 18 }];
    wsRaw['!cols'] = Array(24).fill({ wch: 15 });
    wsHeat['!cols'] = [{ wch: 40 }, { wch: 20 }];
    wsElec['!cols'] = [{ wch: 40 }, { wch: 20 }];

    // Tilføj sheets til workbook i korrekt rækkefølge
    XLSX.utils.book_append_sheet(wb, wsMaster, 'Master');
    XLSX.utils.book_append_sheet(wb, wsRaw, 'Rådata');
    XLSX.utils.book_append_sheet(wb, wsHeat, 'Varme');
    XLSX.utils.book_append_sheet(wb, wsElec, 'El');

    // Generer filnavn med timestamp
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `Fjernvarme-Skaelskoer-Finansiel-Rapport-${timestamp}.xlsx`;

    // Eksportér Excel-fil
    XLSX.writeFile(wb, filename);

    console.log('✅ Excel export successful:', filename);
    return true;

  } catch (error) {
    console.error('❌ Excel export failed:', error);
    throw error;
  }
};
